import React from 'react';
import { Key, Plus, Trash2, Webhook, ExternalLink, CheckCircle2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { CopyIntegrationInstructions } from './CopyIntegrationInstructions';

export const SettingsApiWebhooks = ({
  apiKeys, webhooks,
  setShowCreateApiKey, setShowCreateWebhook,
  handleRevokeApiKey, handleDeleteWebhook, handleToggleWebhook, handleEditApiKey
}) => (
  <div className="space-y-6">
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{fontFamily: 'Manrope'}}><Key className="w-5 h-5 text-amber-600" />API Keys</h3>
          <p className="text-sm text-slate-600">Genera claves para integrar tu CRM u otros sistemas</p>
        </div>
        <Button onClick={() => setShowCreateApiKey(true)} className="bg-amber-600 hover:bg-amber-700"><Plus className="w-4 h-4 mr-2" />Nueva API Key</Button>
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <ExternalLink className="w-5 h-5 text-slate-500" />
          <div className="flex-1 min-w-[260px]">
            <p className="font-medium text-slate-900">Documentación de la API</p>
            <p className="text-sm text-slate-600">Base URL: <code className="bg-slate-200 px-2 py-0.5 rounded text-xs">{window.location.origin}/api/v1</code> · <a href={`${window.location.origin}/api/docs`} target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:underline" data-testid="api-docs-swagger-link">Swagger / OpenAPI</a></p>
            <p className="text-xs text-slate-500 mt-1">Actas: <code className="bg-slate-200 px-1 rounded">POST /v1/actas</code> · <code className="bg-slate-200 px-1 rounded">POST /v1/actas/batch</code> (hasta 500) · <code className="bg-slate-200 px-1 rounded">POST /v1/actas/{'{nro}'}/archivos</code> (pdf + video) · <code className="bg-slate-200 px-1 rounded">GET /v1/actas/{'{nro}'}</code></p>
            <p className="text-xs text-slate-500 mt-1" data-testid="api-docs-contacts-hint">Titular: <code className="bg-slate-200 px-1 rounded">phone</code>, <code className="bg-slate-200 px-1 rounded">email</code> y listas <code className="bg-slate-200 px-1 rounded">phones</code> / <code className="bg-slate-200 px-1 rounded">emails</code> (se suman a la ficha CRM sin borrar los existentes)</p>
          </div>
          <CopyIntegrationInstructions />
        </div>
      </div>
      {apiKeys.length === 0 ? (
        <div className="text-center py-8 text-slate-500"><Key className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>No hay API Keys creadas</p><p className="text-sm">Crea una para comenzar a integrar</p></div>
      ) : (
        <div className="space-y-3">
          {apiKeys.map((key) => (
            <div key={key.id} className={`border rounded-lg p-4 ${key.is_active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 opacity-60'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-slate-900">{key.name}</p>
                  <p className="text-sm text-slate-500 font-mono">{key.key_prefix}...</p>
                  {key.default_portfolio_name && <p className="text-xs text-slate-600 mt-1">Cartera por defecto: <span className="font-medium">{key.default_portfolio_name}</span></p>}
                  <div className="flex flex-wrap gap-1 mt-2">{key.permissions.map((perm) => (<Badge key={perm} variant="outline" className="text-xs">{perm}</Badge>))}</div>
                  <p className="text-xs text-slate-400 mt-2">Creada: {new Date(key.created_at).toLocaleDateString()}{key.last_used_at && ` | Último uso: ${new Date(key.last_used_at).toLocaleDateString()}`}</p>
                </div>
                <div className="flex items-center gap-2">
                  {key.is_active ? <Badge className="bg-emerald-100 text-emerald-700">Activa</Badge> : <Badge className="bg-slate-200 text-slate-600">Revocada</Badge>}
                  <Button variant="ghost" size="sm" onClick={() => handleEditApiKey(key)} className="text-slate-600 hover:bg-slate-100" title="Editar" data-testid={`edit-api-key-${key.id}`}><Pencil className="w-4 h-4" /></Button>
                  {key.is_active && <Button variant="ghost" size="sm" onClick={() => handleRevokeApiKey(key.id)} className="text-red-600 hover:bg-red-50" title="Revocar" data-testid={`revoke-api-key-${key.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>

    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{fontFamily: 'Manrope'}}><Webhook className="w-5 h-5 text-violet-600" />Webhooks</h3>
          <p className="text-sm text-slate-600">Recibe notificaciones en tiempo real cuando ocurren eventos</p>
        </div>
        <Button onClick={() => setShowCreateWebhook(true)} className="bg-violet-600 hover:bg-violet-700"><Plus className="w-4 h-4 mr-2" />Nuevo Webhook</Button>
      </div>
      {webhooks.length === 0 ? (
        <div className="text-center py-8 text-slate-500"><Webhook className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>No hay webhooks configurados</p><p className="text-sm">Configura uno para recibir notificaciones</p></div>
      ) : (
        <div className="space-y-3">
          {webhooks.map((webhook) => (
            <div key={webhook.id} className={`border rounded-lg p-4 ${webhook.is_active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-sm text-slate-900 truncate">{webhook.url}</p>
                  <div className="flex flex-wrap gap-1 mt-2">{webhook.events.map((event) => (<Badge key={event} className="bg-violet-100 text-violet-700 text-xs">{event}</Badge>))}</div>
                  <p className="text-xs text-slate-400 mt-2">{webhook.failure_count > 0 && <span className="text-red-500 mr-2">{webhook.failure_count} errores</span>}{webhook.last_triggered_at && `Último envío: ${new Date(webhook.last_triggered_at).toLocaleDateString()}`}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={webhook.is_active} onCheckedChange={() => handleToggleWebhook(webhook)} />
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteWebhook(webhook.id)} className="text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  </div>
);
